import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Youtube, Wand2, Trash2, Loader2, Music, Check, MessageCircle, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../../components/site/HeroSlides";

const card = "bg-white border border-[var(--line)] rounded-2xl p-5";

function AddVideo({ reload }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [yt, setYt] = useState({ url: "", title: "" });
  async function upload(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); fd.append("title", f.name.replace(/\.[^.]+$/, "")); await api.post("/weddings/mine/videos/upload", fd); toast.success("Video added to your site"); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  async function addYt(e) {
    e.preventDefault();
    try { await api.post("/weddings/mine/videos/youtube", yt); setYt({ url: "", title: "" }); toast.success("YouTube video added"); reload(); } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className={`${card} grid md:grid-cols-2 gap-6`} data-testid="videos-add">
      <div>
        <h3 className="font-serif text-xl mb-2 flex items-center gap-2"><Upload className="w-4 h-4 text-[var(--gold)]" /> Upload a video</h3>
        <p className="text-xs text-[var(--muted)] mb-3">MP4, MOV or WEBM up to 100MB.</p>
        <button onClick={() => ref.current?.click()} disabled={busy} className="btn-gold text-xs" data-testid="videos-upload-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} {busy ? "Uploading…" : "Choose video"}</button>
        <input ref={ref} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={upload} className="hidden" data-testid="videos-upload-input" />
      </div>
      <form onSubmit={addYt} className="space-y-2">
        <h3 className="font-serif text-xl mb-2 flex items-center gap-2"><Youtube className="w-4 h-4 text-[var(--gold)]" /> Paste a YouTube link</h3>
        <input required value={yt.url} onChange={(e) => setYt({ ...yt, url: e.target.value })} placeholder="https://youtube.com/watch?v=…" className="input-luxe" data-testid="videos-yt-url" />
        <input value={yt.title} onChange={(e) => setYt({ ...yt, title: e.target.value })} maxLength={100} placeholder="Title (optional)" className="input-luxe" data-testid="videos-yt-title" />
        <button className="btn-outline-gold text-xs" data-testid="videos-yt-add">Add video</button>
      </form>
    </div>
  );
}

function MemoryBuilder({ reload, busyNow }) {
  const [src, setSrc] = useState([]);
  const [pick, setPick] = useState([]);
  const [f, setF] = useState({ title: "", subtitle: "" });
  const [song, setSong] = useState(null);
  const [busy, setBusy] = useState(false);
  const sref = useRef();
  useEffect(() => { api.get("/weddings/mine/videos/sources").then((r) => setSrc(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setPick(pick.includes(id) ? pick.filter((x) => x !== id) : pick.length < 30 ? [...pick, id] : pick);
  async function upSong(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/weddings/mine/videos/song", fd); setSong(data); toast.success("Song added"); } catch (err) { toast.error(formatApiError(err)); }
  }
  async function make() {
    setBusy(true);
    try { await api.post("/weddings/mine/videos/memory", { ...f, item_ids: pick, song_path: song?.song_path || null }); toast.success("Making your memory video. It shows on your site when it's ready."); setPick([]); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <div className={card} data-testid="memory-builder">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><Wand2 className="w-4 h-4 text-[var(--gold)]" /> Make a memory video</h3>
      <p className="text-xs text-[var(--muted)] mb-4">Pick photos and clips in the order you want them, add your song and a title, and we'll make one video for your site. Photos show for 3 seconds and clips for up to 8.</p>
      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 max-h-72 overflow-y-auto mb-4">
        {src.map((s) => {
          const n = pick.indexOf(s.item_id);
          return (
            <button key={s.item_id} onClick={() => toggle(s.item_id)} className={`relative aspect-square rounded-lg overflow-hidden ${n >= 0 ? "ring-2 ring-[var(--gold)]" : ""}`} data-testid={`memory-src-${s.item_id}`}>
              {s.type === "video" ? <video src={`${fileUrl(s.path)}#t=1`} muted preload="metadata" className="w-full h-full object-cover" /> : <img src={fileUrl(s.path)} alt="" className="w-full h-full object-cover" />}
              {n >= 0 && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[var(--gold)] text-white text-[10px] flex items-center justify-center">{n + 1}</span>}
            </button>
          );
        })}
      </div>
      {!src.length && <p className="text-sm text-[var(--muted)] mb-4">Photos and clips from your wedding feed show up here.</p>}
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={80} placeholder="Title, e.g. Our first look" className="input-luxe" data-testid="memory-title" />
        <input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} maxLength={80} placeholder="Subtitle (optional)" className="input-luxe" data-testid="memory-subtitle" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => sref.current?.click()} className="btn-outline-gold text-xs" data-testid="memory-song-btn"><Music className="w-3 h-3" /> {song ? "Change song" : "Add a song"}</button>
        {song && <span className="text-xs inline-flex items-center gap-1" data-testid="memory-song-name"><Check className="w-3 h-3 text-emerald-600" /> {song.name}</span>}
        <input ref={sref} type="file" accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav" onChange={upSong} className="hidden" data-testid="memory-song-input" />
        <button onClick={make} disabled={busy || busyNow || !pick.length || !f.title.trim()} className="btn-gold text-xs ml-auto disabled:opacity-40 disabled:cursor-not-allowed" data-testid="memory-make">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Make video ({pick.length})</button>
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-2">Only use music you have the rights to share.</p>
    </div>
  );
}

function CommentsAdmin({ v, close, reload }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/public/videos/${v.video_id}/comments`).then((r) => setRows(r.data)).catch(() => {}); }, [v.video_id]);
  async function del(id) {
    try { await api.delete(`/weddings/mine/videos/${v.video_id}/comments/${id}`); setRows(rows.filter((c) => c.comment_id !== id)); reload(); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="mt-3 rounded-xl bg-[var(--bg-warm)] p-3 space-y-2" data-testid={`video-comments-admin-${v.video_id}`}>
      {rows.map((c) => (
        <div key={c.comment_id} className="flex justify-between gap-2 text-sm"><span><b>{c.name}:</b> {c.text}</span><button onClick={() => del(c.comment_id)} className="text-red-600" data-testid={`video-comment-del-${c.comment_id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
      ))}
      {!rows.length && <p className="text-xs text-[var(--muted)]">No comments yet.</p>}
      <button onClick={close} className="text-xs underline">Close</button>
    </div>
  );
}

function VideoRow({ v, reload }) {
  const [title, setTitle] = useState(v.title);
  const [open, setOpen] = useState(false);
  async function rename() {
    if (title.trim() === v.title || !title.trim()) return;
    try { await api.patch(`/weddings/mine/videos/${v.video_id}`, { title }); toast.success("Renamed"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function del() {
    if (!window.confirm("Remove this video from your site?")) return;
    try { await api.delete(`/weddings/mine/videos/${v.video_id}`); reload(); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="py-3 border-b border-[var(--line)]/60" data-testid={`video-row-${v.video_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[var(--bg-warm)]">{v.kind}</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={rename} maxLength={100} className="input-luxe flex-1 min-w-[160px]" data-testid={`video-title-${v.video_id}`} />
        {v.status === "processing" && <span className="text-xs text-[var(--gold)] inline-flex items-center gap-1" data-testid={`video-processing-${v.video_id}`}><Loader2 className="w-3 h-3 animate-spin" /> Making…</span>}
        {v.status === "failed" && <span className="text-xs text-red-600">Failed: {v.error}</span>}
        {v.status === "ready" && <span className="text-xs text-[var(--muted)]">{v.views} view{v.views === 1 ? "" : "s"} · {v.likes} like{v.likes === 1 ? "" : "s"}</span>}
        <button onClick={() => setOpen(!open)} className="text-xs inline-flex items-center gap-1" data-testid={`video-comments-${v.video_id}`}><MessageCircle className="w-3.5 h-3.5" /> {v.comments}</button>
        <button onClick={del} className="p-2 text-red-600" data-testid={`video-del-${v.video_id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && <CommentsAdmin v={v} close={() => setOpen(false)} reload={reload} />}
    </div>
  );
}

export default function VideosPage() {
  const [vids, setVids] = useState(null);
  const [slug, setSlug] = useState("");
  const load = () => api.get("/weddings/mine/videos").then((r) => setVids(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); api.get("/weddings/mine").then((r) => setSlug(r.data.slug)).catch(() => {}); }, []);
  const processing = (vids || []).some((v) => v.status === "processing");
  useEffect(() => { if (!processing) return undefined; const t = setInterval(load, 6000); return () => clearInterval(t); }, [processing]);
  if (!vids) return null;
  return (
    <div className="space-y-5" data-testid="videos-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Your site</p>
          <h1 className="font-serif font-light text-4xl">Video Memories</h1>
          <p className="text-[var(--muted)] mt-2 text-sm">Videos show in a player with a playlist on your wedding site, where guests can watch, like and comment.</p>
        </div>
        {slug && <a href={`/site/${slug}#videos`} target="_blank" rel="noreferrer" className="btn-outline-gold text-xs" data-testid="videos-view-site"><ExternalLink className="w-3 h-3" /> View on my site</a>}
      </div>
      <AddVideo reload={load} />
      <MemoryBuilder reload={load} busyNow={processing} />
      <div className={card} data-testid="videos-list">
        <h3 className="font-serif text-xl mb-2">Your videos ({vids.length})</h3>
        {vids.map((v) => <VideoRow key={v.video_id} v={v} reload={load} />)}
        {!vids.length && <p className="text-sm text-[var(--muted)]">No videos yet.</p>}
      </div>
    </div>
  );
}
